/**
 * O contexto da tela sendo desenhada, disponível a qualquer renderer por injeção.
 *
 * Existe para manter a promessa do `NodeHost`: **as entradas de um renderer são
 * sempre as mesmas três** (`node`, `form`, `mode`). Sem essa regra, o despachante
 * precisaria saber o que cada tipo precisa — que é exatamente o `ngSwitch` de
 * quatro níveis que estamos matando, só que reescrito em TypeScript.
 *
 * Quando um renderer precisa de mais do que o próprio nó — a grade precisa do
 * documento inteiro para achar os painéis de mestre-detalhe, por exemplo — ele
 * injeta isto. O `ScreenRendererComponent` provê uma instância POR TELA, e não em
 * root, porque duas telas podem estar vivas ao mesmo tempo: é o caso do
 * `simple-datagrid`, que abre grade pai e filha simultaneamente.
 */
import { Injectable, signal } from '@angular/core';

import { ScreenDocument } from '../model/screen-schema';
import { ScreenIndex, buildIndex } from '../model/screen-index';

@Injectable()
export class ScreenContext {

  private readonly _document = signal<ScreenDocument | null>(null);
  private readonly _data = signal<unknown>(null);

  readonly document = this._document.asReadonly();

  /**
   * O dado que alimentou a tela.
   *
   * Mora aqui pelo mesmo motivo do documento: é mais do que o próprio nó, e
   * as entradas de um renderer são sempre as mesmas três. Quem precisa é o
   * cartão de dashboard no modo `dataPath` — gráfico sobre a mesma lista que a
   * grade da tela já mostra, sem uma segunda ida ao banco.
   */
  readonly data = this._data.asReadonly();

  /**
   * Índice derivado, construído sob demanda e memoizado por identidade do
   * documento. Só paga quem usa: tela sem grade nunca constrói índice.
   */
  private cachedIndex: ScreenIndex | null = null;
  private cachedFor: ScreenDocument | null = null;

  setDocument(document: ScreenDocument | null): void {
    this._document.set(document);
  }

  setData(data: unknown): void {
    this._data.set(data);
  }

  index(): ScreenIndex | null {
    const document = this._document();

    if (document === null) {
      return null;
    }
    if (this.cachedFor === document && this.cachedIndex !== null) {
      return this.cachedIndex;
    }

    this.cachedIndex = buildIndex(document);
    this.cachedFor = document;
    return this.cachedIndex;
  }
}
